import { defaultDescription, defaultOgImage, siteName, titleSeparator, type PageHead } from './head';

export type ResolvedHead = {
  title: string;
  description: string;
  canonical: string;
  ogTitle: string;
  ogDescription: string;
  ogUrl: string;
  ogType: 'website' | 'article';
  ogImage: string;
  noIndex: boolean;
  jsonLd: Record<string, unknown>[];
};

/** Format a `PageHead` into concrete strings the root layout can render.
 *  Title format: `{leaf} | {parent} | … | StuffPack`. The site name is
 *  appended unless the leaf already contains it (legal/marketing pages
 *  whose title is "StuffPack — …"). */
export function resolveHead(head: PageHead | undefined, url: URL): ResolvedHead {
  const segments = head?.titleSegments?.filter((s): s is string => Boolean(s)) ?? [];
  const titleParts = segments.length > 0 ? segments : head?.title ? [head.title] : [];
  const joined = titleParts.join(titleSeparator);
  const title = joined ? (joined.includes(siteName) ? joined : `${joined}${titleSeparator}${siteName}`) : siteName;

  const description = head?.description ?? defaultDescription;
  const canonicalPath = head?.canonicalPath ?? url.pathname;
  const canonical = `${url.origin}${canonicalPath}`;
  const ogImage = head?.og?.image ?? defaultOgImage;
  const ogImageUrl = ogImage.startsWith('http') ? ogImage : `${url.origin}${ogImage}`;
  const ogType = head?.og?.type ?? 'website';

  return {
    title,
    description,
    canonical,
    ogTitle: title,
    ogDescription: description,
    ogUrl: canonical,
    ogType,
    ogImage: ogImageUrl,
    noIndex: head?.noIndex ?? false,
    jsonLd: head?.jsonLd ?? [],
  };
}
